import { useCallback, useEffect, useState } from 'react'
import { useUserRole } from './useUserRole'

/**
 * `show_sandbox_reports_only`, held client-side (CR-341 full cycle).
 *
 * The spec leaves the storage of this preference open — a new table, a JSON column on `users`, or
 * client-held with a server default — and names client-held as one of the three. Held here until
 * that is decided, which makes it per-browser rather than per user; swapping it for a served
 * preference is a change of source, not of behaviour.
 *
 * Two surfaces read it — the reports project/term picker and the report actions menu — so the
 * value lives in one place and both are told when it moves. `storage` only fires in *other* tabs,
 * so the write also dispatches its own event for the components sharing this one.
 */
const STORAGE_KEY = 'reportsShowSandboxOnly'
const CHANGE_EVENT = 'sandbox-reports-view-change'

/**
 * What the last write intended, whether or not the browser accepted it.
 *
 * `localStorage` throws in a private window and with site data blocked, and the self-notify below
 * would then re-read the *old* value and snap the control back under the user's finger. Holding
 * the intent in the module keeps the choice for the session, which is what the failed write costs
 * and no more. Found by review, 2026-09-21.
 */
let intended: boolean | null = null

const read = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    // The real world is the safe default: a reader who cannot persist the preference sees
    // ordinary reports.
    return intended ?? false
  }
}

export const useSandboxReportsView = (): { showSandboxOnly: boolean, setShowSandboxOnly: (next: boolean) => void } => {
  const { hasGlobalRole } = useUserRole()
  const [stored, setState] = useState<boolean>(read)

  useEffect(() => {
    const sync = (): void => { setState(read()) }
    window.addEventListener(CHANGE_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const setShowSandboxOnly = useCallback((next: boolean) => {
    intended = next
    setState(next)
    try {
      if (next) localStorage.setItem(STORAGE_KEY, 'true')
      else localStorage.removeItem(STORAGE_KEY)
    } catch {
      // The choice still applies to this session, through `intended` above.
    }
    window.dispatchEvent(new Event(CHANGE_EVENT))
  }, [])

  /**
   * Honoured only for a caller who holds a Global-level role, because only they can be shown a
   * sandboxed term at all. The controls are gated the same way, but this value outlives them: it
   * is per browser, so a stale `true` — a shared machine, or a role since removed — would
   * otherwise keep filtering every reports read for someone with no control left to turn it off,
   * and empty lists with no explanation. Found by review, 2026-09-21.
   */
  return { showSandboxOnly: hasGlobalRole && stored, setShowSandboxOnly }
}

/**
 * The preference as a request parameter, for the reads the backend filters.
 *
 * Named for the backend's own field (`show_sandbox_reports_only`) rather than translated, so the
 * two halves of the contract read the same. Omitted entirely when off, which now means
 * `sandbox=exclude`, the backend's default since 2026-09-23: real projects only, plus projects
 * with no terms yet. That is the right answer for Reports — off should not offer rehearsals — but
 * it is no longer a neutral "ordinary path", and any other caller of that endpoint which wants
 * rehearsals back has to ask for `sandbox=include` (see `ProjectPage`).
 *
 * `show_sandbox_reports_only=true` still maps to `sandbox=only`, so this parameter keeps working
 * whichever side deploys first. An explicit `sandbox` beats it; an unrecognised value is a 400.
 *
 * Spread into a params object — `{ ...sandboxReportsRequestParam(on) }` — and add the boolean to
 * the query key beside it, or React Query answers one world's request from the other's cache.
 */
export const sandboxReportsRequestParam = (showSandboxOnly: boolean): Record<string, boolean> => (
  showSandboxOnly ? { show_sandbox_reports_only: true } : {}
)
